import { computed, ref, watch } from 'vue'
import { useScrollLock } from '@vueuse/core'

import type { ComputedRef } from 'vue'
import type { SidebarMenuProps } from '@UI/Menus/SidebarMenu/types/interfaces'

import { AppRoute } from '@ts-routes/enums'

interface UseSidebarMenuOptions {
  props: SidebarMenuProps
  onClose: () => void
}

interface UseSidebarMenuReturn {
  refIsOpen: ComputedRef<boolean>
  refDirection: ComputedRef<'left' | 'right'>
  homePath: AppRoute.Homepage
  setMenu: (action: boolean) => void
  handleClose: () => void
}

export const useSidebarMenu = ({ props, onClose }: UseSidebarMenuOptions): UseSidebarMenuReturn => {
  const refIsHovered = ref(false)
  const el = ref<HTMLElement | null>(window.document.body)
  const scrollLock = useScrollLock(el)

  // Hover expands the desktop rail; the open drawer always shows the expanded menu.
  const refIsOpen = computed<boolean>(() => refIsHovered.value || Boolean(props.isDrawerOpen))
  const refDirection = computed<'left' | 'right'>(() => refIsOpen.value ? 'left' : 'right')

  const setMenu = (action: boolean): void => {
    if (props.isDrawerOpen) return

    refIsHovered.value = action
  }

  const handleClose = (): void => {
    if (props.isDrawerOpen) onClose()
  }

  watch(refIsOpen, (value) => {
    scrollLock.value = value
  })

  return {
    refIsOpen,
    refDirection,
    homePath: AppRoute.Homepage,
    setMenu,
    handleClose,
  }
}
